import { useMemo, useState } from "react";
import type { SourceNode } from "../types";
import { LIFECYCLE_OPTIONS, defaultStatusForKind, statusOptionsForKind } from "../lib/lifecycle";
import { StatusBadge } from "./StatusBadge";

type Props = {
  sources: SourceNode[];
  onUpdateSource: (id: string, input: { status?: string }) => Promise<void> | void;
};

function statusOf(source: SourceNode) {
  return source.status ?? defaultStatusForKind(source.kind);
}

export function StatusBoard({ sources, onUpdateSource }: Props) {
  // Sources all share the "source" interface lifecycle. Cards live in the column
  // matching their stored lifecycle slug; drag or the dropdown moves them.
  const columns = LIFECYCLE_OPTIONS.source;
  const [dragId, setDragId] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [overSlug, setOverSlug] = useState<string | null>(null);

  const byStatus = useMemo(() => {
    const map = new Map<string, SourceNode[]>();
    for (const column of columns) {
      map.set(column.slug, []);
    }
    for (const source of sources) {
      const slug = statusOf(source);
      if (!map.has(slug)) {
        map.set(slug, []);
      }
      map.get(slug)!.push(source);
    }
    return map;
  }, [columns, sources]);

  const changeStatus = async (source: SourceNode, nextStatus: string) => {
    if (statusOf(source) === nextStatus) {
      return;
    }
    setPendingId(source.id);
    try {
      await onUpdateSource(source.id, { status: nextStatus });
    } finally {
      setPendingId(null);
    }
  };

  const handleDrop = async (slug: string) => {
    const id = dragId;
    setDragId(null);
    setOverSlug(null);
    if (!id) {
      return;
    }
    const source = sources.find((node) => node.id === id);
    if (source) {
      await changeStatus(source, slug);
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-slate-50 p-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {columns.map((column) => {
          const items = byStatus.get(column.slug) ?? [];
          const isOver = overSlug === column.slug;

          return (
            <div
              key={column.slug}
              className={`flex min-h-[420px] flex-col rounded-lg border bg-white transition-colors ${
                isOver ? "border-blue-300 ring-2 ring-blue-200" : "border-line"
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                if (overSlug !== column.slug) {
                  setOverSlug(column.slug);
                }
              }}
              onDragLeave={() => setOverSlug((current) => (current === column.slug ? null : current))}
              onDrop={() => handleDrop(column.slug)}
            >
              <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: column.dotColor }} aria-hidden="true" />
                  <span className="text-xs font-bold text-ink">{column.label}</span>
                </div>
                <span className="rounded-md border border-line bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-muted">
                  {items.length}
                </span>
              </div>

              <div className="flex-1 space-y-2 p-2">
                {items.length === 0 ? (
                  <p className="rounded-md border border-dashed border-line bg-slate-50/60 px-3 py-6 text-center text-[11px] font-semibold text-muted">
                    Kéo thẻ vào đây
                  </p>
                ) : (
                  items.map((source) => (
                    <article
                      key={source.id}
                      draggable
                      onDragStart={() => setDragId(source.id)}
                      onDragEnd={() => {
                        setDragId(null);
                        setOverSlug(null);
                      }}
                      className={`cursor-grab rounded-lg border border-line bg-white p-3 shadow-sm transition-opacity active:cursor-grabbing ${
                        dragId === source.id ? "opacity-50" : "opacity-100"
                      } ${pendingId === source.id ? "animate-pulse" : ""}`}
                    >
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <StatusBadge kind={source.kind} />
                        <span className="text-[11px] font-semibold text-muted">{source.progress}%</span>
                      </div>
                      <p className="line-clamp-2 text-sm font-bold leading-5 text-ink" title={source.title}>
                        {source.title}
                      </p>
                      <select
                        className="mt-2 w-full rounded-md border border-line bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600"
                        value={statusOf(source)}
                        disabled={pendingId === source.id}
                        onChange={(event) => changeStatus(source, event.target.value)}
                      >
                        {statusOptionsForKind(source.kind).map((option) => (
                          <option key={option.slug} value={option.slug}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </article>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
